import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, AlertTriangle, ChevronDown } from "lucide-react";
import { createCustomer, updateCustomer, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = {
    customer_type: "privat", name: "", phone: "", email: "", address: "",
    postal_code: "", city: "", company_name: "", contact_person: "", notes: "",
};

export default function CustomerFormDialog({ open, customer, onClose, onSaved }) {
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [more, setMore] = useState(false);
    const [dup, setDup] = useState(null);
    const isEdit = !!customer?.id;

    useEffect(() => {
        if (open) { setForm(customer ? { ...EMPTY, ...customer } : EMPTY); setDup(null); setMore(false); }
    }, [open, customer]);

    const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const save = async (force) => {
        setSaving(true);
        try {
            const payload = { ...form, force: !!force };
            const res = isEdit ? await updateCustomer(customer.id, payload) : await createCustomer(payload);
            if (!isEdit && res && res.duplicate) { setDup(res.existing); setSaving(false); return; }
            toast.success(isEdit ? "Kunde opdateret" : "Kunde oprettet");
            onSaved(res);
            onClose();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const submit = (e) => { e.preventDefault(); save(false); };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>{isEdit ? "Redigér kunde" : "Ny kunde"}</DialogTitle></DialogHeader>

                {dup && (
                    <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-3 mb-2" data-testid="customer-dup-warning">
                        <p className="text-sm font-bold text-amber-800 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> Mulig eksisterende kunde fundet</p>
                        <div className="text-xs text-amber-900 mt-1.5">
                            <p><b>{dup.name}</b></p>
                            <p>{dup.phone || "—"} · {dup.email || "—"}</p>
                            <p>{dup.address || "—"}</p>
                        </div>
                        <div className="flex gap-2 mt-3">
                            <Button type="button" size="sm" variant="outline" data-testid="customer-dup-use"
                                onClick={() => { toast.info("Bruger eksisterende kunde"); onSaved(dup); onClose(); }}>Brug eksisterende kunde</Button>
                            <Button type="button" size="sm" data-testid="customer-dup-force" disabled={saving}
                                onClick={() => save(true)} className="bg-amber-600 text-white">Opret alligevel</Button>
                        </div>
                    </div>
                )}

                <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-1">
                    <div className="sm:col-span-2">
                        <Label>Kundetype</Label>
                        <div className="flex gap-1.5 mt-1">
                            {[{ v: "privat", l: "Privat" }, { v: "erhverv", l: "Erhverv" }].map((t) => (
                                <button key={t.v} type="button" data-testid={`customer-type-${t.v}`}
                                    onClick={() => setForm((f) => ({ ...f, customer_type: t.v }))}
                                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border ${form.customer_type === t.v ? "bg-blue-50 border-blue-300 text-blue-700" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{t.l}</button>
                            ))}
                        </div>
                    </div>
                    <div className="sm:col-span-2">
                        <Label>Navn</Label>
                        <Input required data-testid="customer-form-name" value={form.name} onChange={upd("name")} className="h-10 mt-1" />
                    </div>
                    <div>
                        <Label>Telefon</Label>
                        <Input data-testid="customer-form-phone" value={form.phone} onChange={upd("phone")} className="h-10 mt-1" />
                    </div>
                    <div>
                        <Label>Email</Label>
                        <Input type="email" data-testid="customer-form-email" value={form.email} onChange={upd("email")} className="h-10 mt-1" />
                    </div>
                    <div className="sm:col-span-2">
                        <Label>Adresse</Label>
                        <Input data-testid="customer-form-address" value={form.address} onChange={upd("address")} className="h-10 mt-1" />
                    </div>
                    <div>
                        <Label>Postnummer</Label>
                        <Input data-testid="customer-form-postal" value={form.postal_code} onChange={upd("postal_code")} className="h-10 mt-1" />
                    </div>
                    <div>
                        <Label>By</Label>
                        <Input data-testid="customer-form-city" value={form.city} onChange={upd("city")} className="h-10 mt-1" />
                    </div>

                    <div className="sm:col-span-2">
                        <button type="button" onClick={() => setMore((m) => !m)} data-testid="customer-more-toggle"
                            className="text-xs text-slate-500 inline-flex items-center gap-1 hover:text-slate-800">
                            <ChevronDown className={`w-3.5 h-3.5 transition ${more ? "rotate-180" : ""}`} /> Flere oplysninger
                        </button>
                    </div>
                    {more && (
                        <>
                            <div>
                                <Label>Virksomhedsnavn</Label>
                                <Input data-testid="customer-form-company" value={form.company_name} onChange={upd("company_name")} className="h-10 mt-1" />
                            </div>
                            <div>
                                <Label>Kontaktperson</Label>
                                <Input data-testid="customer-form-contact" value={form.contact_person} onChange={upd("contact_person")} className="h-10 mt-1" />
                            </div>
                            <div className="sm:col-span-2">
                                <Label>Noter</Label>
                                <Textarea data-testid="customer-form-notes" value={form.notes} onChange={upd("notes")} className="min-h-[70px] mt-1" />
                            </div>
                        </>
                    )}

                    <div className="sm:col-span-2 flex justify-end gap-2 mt-2">
                        <Button type="button" variant="outline" onClick={onClose}>Annullér</Button>
                        <Button type="submit" disabled={saving} data-testid="customer-form-submit" className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                            {isEdit ? "Gem ændringer" : "Opret kunde"}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
